import React from 'react';
import { IonIcon } from '@ionic/react';
import type { ICellRendererParams } from 'ag-grid-community';
import { add, banOutline, checkmarkOutline, closeOutline, createOutline, listOutline, lockClosedOutline, lockOpenOutline } from 'ionicons/icons';
import { ActionGridActionColumn, ActionGridBulkAction, ActionGridColumnDefinition } from '@/types/model/actionGrid';
import {
  PlanningGroup,
  PlanningGroupGridRow,
  PlanningInspectionPlanGridRow,
  PlanningLineItemGridRow,
} from '@/services/PlanningService';

// ── Groups tab ───────────────────────────────────────────────────────────────

export const buildPlanningGroupGridRows = (groups: PlanningGroup[]): PlanningGroupGridRow[] => {
  return groups.map((group) => ({
    id: group.id,
    groupIdentifier: group.groupIdentifier,
    title: group.title,
    lineItemCount: group.lineItems?.length ?? 0,
  }));
};

export const buildPlanningGroupGridColumns = (): ActionGridColumnDefinition[] => [
  { key: 'id', headerName: 'ID', isRowId: true, colDef: { width: 90, filter: 'agNumberColumnFilter' } },
  { key: 'groupIdentifier', headerName: 'Group #', colDef: { width: 110, filter: true } },
  { key: 'title', headerName: 'Title', colDef: { flex: 1, minWidth: 200, filter: true, tooltipField: 'title' } },
  { key: 'lineItemCount', headerName: 'Line Items', colDef: { width: 120, filter: 'agNumberColumnFilter' } },
];

export const buildPlanningGroupGridData = (rows: PlanningGroupGridRow[]): unknown[][] => {
  const columns = buildPlanningGroupGridColumns();
  return rows.map((row) => columns.map((column) => (row as unknown as Record<string, unknown>)[column.key]));
};

interface BuildPlanningGroupGridActionColumnsParams {
  rows: PlanningGroupGridRow[];
  onEdit: (row: PlanningGroupGridRow) => void;
}

export const buildPlanningGroupGridActionColumns = ({
  rows,
  onEdit,
}: BuildPlanningGroupGridActionColumnsParams): ActionGridActionColumn[] => {
  const rowsById = new Map(rows.map((row) => [String(row.id), row]));

  return [
    {
      id: 'group-row',
      headerName: 'Actions',
      width: 70,
      pinned: 'right',
      actions: [
        {
          id: 'edit-group',
          title: 'Edit Group',
          icon: createOutline,
          onAction: ({ row }) => {
            const found = rowsById.get(row.rowId);
            if (found) {
              onEdit(found);
            }
          },
        },
      ],
    },
  ];
};

interface BuildPlanningGroupGridBulkActionsParams {
  canAddGroup: boolean;
  onAddNew: () => void;
  onSwitchToAccordion: () => void;
}

export const buildPlanningGroupGridBulkActions = ({
  canAddGroup,
  onAddNew,
  onSwitchToAccordion,
}: BuildPlanningGroupGridBulkActionsParams): ActionGridBulkAction[] => {
  const actions: ActionGridBulkAction[] = [];

  if (canAddGroup) {
    actions.push({
      id: 'new-group',
      label: 'New Group',
      group: 1,
      order: 1,
      icon: add,
      onAction: () => onAddNew(),
    });
  }

  actions.push({
    id: 'switch-to-accordion-view',
    label: 'Switch to Accordion View',
    group: 2,
    order: 1,
    icon: listOutline,
    onAction: () => onSwitchToAccordion(),
  });

  return actions;
};

// ── Line Items tab ───────────────────────────────────────────────────────────

export const buildPlanningLineItemGridRows = (groups: PlanningGroup[]): PlanningLineItemGridRow[] => {
  const rows: PlanningLineItemGridRow[] = [];

  for (const group of groups) {
    for (const lineItem of group.lineItems ?? []) {
      rows.push({
        id: lineItem.id,
        groupId: group.id,
        groupIdentifier: group.groupIdentifier,
        groupTitle: group.title,
        lineItem: lineItem.lineItem,
        title: lineItem.title,
        majorRequirement: lineItem.majorRequirement,
        isActive: lineItem.isActive,
        modificationStatus: lineItem.modificationStatus,
        lastUpdated: lineItem.lastUpdated,
        attachmentCount: lineItem.attachmentCount,
      });
    }
  }

  return rows;
};

export const buildPlanningLineItemGridColumns = (): ActionGridColumnDefinition[] => [
  { key: 'id', headerName: 'ID', isRowId: true, colDef: { width: 90, filter: 'agNumberColumnFilter' } },
  { key: 'groupTitle', headerName: 'Group', colDef: { width: 160, filter: true } },
  { key: 'lineItem', headerName: 'Line Item', colDef: { width: 110, filter: true } },
  { key: 'title', headerName: 'Title', colDef: { flex: 1, minWidth: 180, filter: true, tooltipField: 'title' } },
  { key: 'majorRequirement', headerName: 'Major Requirement', colDef: { flex: 1, minWidth: 160, filter: true, tooltipField: 'majorRequirement' } },
  {
    key: 'isActive',
    headerName: 'Status',
    colDef: { width: 110, filter: true, valueFormatter: (p) => (p.value ? 'Active' : 'Inactive') },
  },
  { key: 'modificationStatus', headerName: 'Modification Status', colDef: { width: 150, filter: true } },
  {
    key: 'lastUpdated',
    headerName: 'Last Updated',
    colDef: { width: 130, filter: 'agDateColumnFilter', valueFormatter: (p) => (p.value ? new Date(p.value as string).toLocaleDateString() : '—') },
  },
  { key: 'attachmentCount', headerName: 'Attachments', colDef: { width: 110, filter: 'agNumberColumnFilter' } },
];

export const buildPlanningLineItemGridData = (rows: PlanningLineItemGridRow[]): unknown[][] => {
  const columns = buildPlanningLineItemGridColumns();
  return rows.map((row) => columns.map((column) => (row as unknown as Record<string, unknown>)[column.key]));
};

interface BuildPlanningLineItemGridActionColumnsParams {
  rows: PlanningLineItemGridRow[];
  onEdit: (row: PlanningLineItemGridRow) => void;
}

export const buildPlanningLineItemGridActionColumns = ({
  rows,
  onEdit,
}: BuildPlanningLineItemGridActionColumnsParams): ActionGridActionColumn[] => {
  const rowsById = new Map(rows.map((row) => [String(row.id), row]));

  return [
    {
      id: 'line-item-row',
      headerName: 'Actions',
      width: 70,
      pinned: 'right',
      actions: [
        {
          id: 'edit-line-item',
          title: 'Edit Line Item',
          icon: createOutline,
          onAction: ({ row }) => {
            const found = rowsById.get(row.rowId);
            if (found) {
              onEdit(found);
            }
          },
        },
      ],
    },
  ];
};

interface BuildPlanningLineItemGridBulkActionsParams {
  canAddLineItem: boolean;
  canActivateLineItem: boolean;
  onAddNew: (groupId?: number) => void;
  onActivate: (ids: number[]) => void;
  onDeactivate: (ids: number[]) => void;
  onSwitchToAccordion: () => void;
}

export const buildPlanningLineItemGridBulkActions = ({
  canAddLineItem,
  canActivateLineItem,
  onAddNew,
  onActivate,
  onDeactivate,
  onSwitchToAccordion,
}: BuildPlanningLineItemGridBulkActionsParams): ActionGridBulkAction[] => {
  const actions: ActionGridBulkAction[] = [];

  if (canAddLineItem) {
    actions.push({
      id: 'new-line-item',
      label: 'New Line Item',
      group: 1,
      order: 1,
      icon: add,
      onAction: ({ selectedRows }) => {
        const groupIds = new Set(selectedRows.map((row) => Number(row.rowObject.groupId)));
        const groupId = groupIds.size === 1 ? Array.from(groupIds)[0] : undefined;
        onAddNew(groupId);
      },
    });
  }

  if (canActivateLineItem) {
    actions.push(
      {
        id: 'activate-line-items',
        label: 'Activate',
        group: 2,
        order: 1,
        icon: checkmarkOutline,
        isDisabled: ({ selectedRows }) => selectedRows.length === 0,
        onAction: ({ selectedRows }) => onActivate(selectedRows.map((row) => Number(row.rowId))),
      },
      {
        id: 'inactivate-line-items',
        label: 'Inactivate',
        group: 2,
        order: 2,
        icon: closeOutline,
        isDisabled: ({ selectedRows }) => selectedRows.length === 0,
        onAction: ({ selectedRows }) => onDeactivate(selectedRows.map((row) => Number(row.rowId))),
      }
    );
  }

  actions.push({
    id: 'switch-to-accordion-view',
    label: 'Switch to Accordion View',
    group: 3,
    order: 1,
    icon: listOutline,
    onAction: () => onSwitchToAccordion(),
  });

  return actions;
};

// ── Inspection Plans tab ─────────────────────────────────────────────────────

export const buildPlanningInspectionPlanGridRows = (groups: PlanningGroup[]): PlanningInspectionPlanGridRow[] => {
  const rows: PlanningInspectionPlanGridRow[] = [];

  for (const group of groups) {
    for (const lineItem of group.lineItems ?? []) {
      const plan = lineItem.inspectionPlan;
      if (!plan) {
        continue;
      }

      rows.push({
        id: plan.id,
        lineItemId: lineItem.id,
        groupId: group.id,
        groupIdentifier: group.groupIdentifier,
        groupTitle: group.title,
        lineItem: lineItem.lineItem,
        lineItemTitle: lineItem.title,
        isActive: plan.isActive,
        isLocked: plan.isLocked,
        status: plan.status,
        primaryValue: plan.primaryValue ?? '',
        alternateValue: plan.alternateValue ?? '',
        timeToInspect: plan.timeToInspect ?? '',
        sampleSize: plan.sampleSize ?? '',
        scheduledInspectionsCount: plan.scheduledInspectionsCount ?? 0,
        checklistItemCount: plan.checklistItems?.length ?? 0,
      });
    }
  }

  return rows;
};

export const buildPlanningInspectionPlanGridColumns = (): ActionGridColumnDefinition[] => [
  { key: 'id', headerName: 'ID', isRowId: true, colDef: { width: 90, filter: 'agNumberColumnFilter' } },
  { key: 'groupTitle', headerName: 'Group', colDef: { width: 160, filter: true } },
  { key: 'lineItem', headerName: 'Line Item', colDef: { width: 110, filter: true } },
  { key: 'lineItemTitle', headerName: 'Line Item Title', colDef: { flex: 1, minWidth: 180, filter: true, tooltipField: 'lineItemTitle' } },
  {
    key: 'isActive',
    headerName: 'Status',
    colDef: {
      width: 90,
      filter: true,
      cellRenderer: (p: ICellRendererParams) => p.value
        ? null
        : React.createElement(IonIcon, {
            icon: banOutline,
            title: 'Inactive',
            'aria-label': 'Inactive',
            style: { fontSize: '18px', color: 'var(--ion-color-medium)' },
          }),
    },
  },
  {
    key: 'isLocked',
    headerName: 'Locked',
    colDef: { width: 100, filter: true, valueFormatter: (p) => (p.value ? 'Locked' : 'Unlocked') },
  },
  { key: 'primaryValue', headerName: 'Primary', colDef: { width: 110, filter: true } },
  { key: 'alternateValue', headerName: 'Alternate', colDef: { width: 110, filter: true } },
  { key: 'timeToInspect', headerName: 'Hours', colDef: { width: 100, filter: true } },
  { key: 'sampleSize', headerName: 'Sample Size', colDef: { width: 130, filter: true } },
  { key: 'scheduledInspectionsCount', headerName: 'Scheduled', colDef: { width: 110, filter: 'agNumberColumnFilter' } },
  { key: 'checklistItemCount', headerName: 'Checklist Items', colDef: { width: 130, filter: 'agNumberColumnFilter' } },
];

export const buildPlanningInspectionPlanGridData = (rows: PlanningInspectionPlanGridRow[]): unknown[][] => {
  const columns = buildPlanningInspectionPlanGridColumns();
  return rows.map((row) => columns.map((column) => (row as unknown as Record<string, unknown>)[column.key]));
};

interface BuildPlanningInspectionPlanGridActionColumnsParams {
  rows: PlanningInspectionPlanGridRow[];
  onEdit: (row: PlanningInspectionPlanGridRow) => void;
}

export const buildPlanningInspectionPlanGridActionColumns = ({
  rows,
  onEdit,
}: BuildPlanningInspectionPlanGridActionColumnsParams): ActionGridActionColumn[] => {
  const rowsById = new Map(rows.map((row) => [String(row.id), row]));

  return [
    {
      id: 'inspection-plan-row',
      headerName: 'Actions',
      width: 70,
      pinned: 'right',
      actions: [
        {
          id: 'edit-inspection-plan',
          title: 'Edit Inspection Plan',
          icon: createOutline,
          onAction: ({ row }) => {
            const found = rowsById.get(row.rowId);
            if (found) {
              onEdit(found);
            }
          },
        },
      ],
    },
  ];
};

interface BuildPlanningInspectionPlanGridBulkActionsParams {
  canActivateInspectionPlan: boolean;
  canLockInspectionPlan: boolean;
  canUnlockInspectionPlan: boolean;
  onActivate: (ids: number[]) => void;
  onDeactivate: (ids: number[]) => void;
  onLock: (ids: number[]) => void;
  onUnlock: (ids: number[]) => void;
  onSwitchToAccordion: () => void;
}

export const buildPlanningInspectionPlanGridBulkActions = ({
  canActivateInspectionPlan,
  canLockInspectionPlan,
  canUnlockInspectionPlan,
  onActivate,
  onDeactivate,
  onLock,
  onUnlock,
  onSwitchToAccordion,
}: BuildPlanningInspectionPlanGridBulkActionsParams): ActionGridBulkAction[] => {
  const actions: ActionGridBulkAction[] = [];

  if (canActivateInspectionPlan) {
    actions.push(
      {
        id: 'activate-inspection-plans',
        group: 2,
        order: 1,
        label: 'Activate',
        icon: checkmarkOutline,
        isDisabled: ({ selectedRows }) => selectedRows.length === 0,
        onAction: ({ selectedRows }) => onActivate(selectedRows.map((row) => Number(row.rowId))),
      },
      {
        id: 'inactivate-inspection-plans',
        group: 2,
        order: 2,
        label: 'Inactivate',
        icon: closeOutline,
        isDisabled: ({ selectedRows }) => selectedRows.length === 0,
        onAction: ({ selectedRows }) => onDeactivate(selectedRows.map((row) => Number(row.rowId))),
      }
    );
  }

  if (canLockInspectionPlan) {
    actions.push({
      id: 'lock-inspection-plans',
      group: 2,
      order: 3,
      label: 'Lock',
      icon: lockClosedOutline,
      isDisabled: ({ selectedRows }) => selectedRows.length === 0,
      onAction: ({ selectedRows }) => onLock(selectedRows.map((row) => Number(row.rowId))),
    });
  }

  if (canUnlockInspectionPlan) {
    actions.push({
      id: 'unlock-inspection-plans',
      group: 2,
      order: 4,
      label: 'Unlock',
      icon: lockOpenOutline,
      isDisabled: ({ selectedRows }) => selectedRows.length === 0,
      onAction: ({ selectedRows }) => onUnlock(selectedRows.map((row) => Number(row.rowId))),
    });
  }

  actions.push({
    id: 'switch-to-accordion-view',
    group: 3,
    order: 1,
    label: 'Switch to Accordion View',
    icon: listOutline,
    onAction: () => onSwitchToAccordion(),
  });

  return actions;
};
